/**
 * DATE-ONLY POLICY
 * =================
 * Date-only fields (e.g., game dates, fight dates) represent calendar days,
 * NOT instants in time. They must display the same day regardless of timezone.
 *
 * Rules:
 * 1. API responses return date-only fields as "YYYY-MM-DD" strings
 * 2. Frontend NEVER parses date-only strings with `new Date()` or `parseISO()`
 *    because these interpret the string as UTC midnight, causing timezone shifts
 * 3. Date-only strings are parsed manually to extract year/month/day components
 * 4. For display, we format directly from components without timezone conversion
 *
 * Date-time fields (e.g., createdAt, timestamps) DO represent instants and
 * should use parseISO + formatDistanceToNow for relative display.
 */

const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Returns the number of days in a given month, accounting for leap years.
 */
const getDaysInMonth = (year, month) => {
    const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 2) {
        const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        return isLeap ? 29 : 28;
    }
    return daysPerMonth[month - 1];
};

/**
 * Parses a date-only string into components WITHOUT timezone conversion.
 * Handles both "YYYY-MM-DD" and ISO datetime strings (extracts date portion only).
 * Also accepts non-zero-padded formats like "2024-1-5" for legacy data.
 *
 * @param {string} dateString - Date string in "YYYY-MM-DD" or ISO format
 * @returns {{ year: number, month: number, day: number } | null}
 */
export const parseDateOnly = (dateString) => {
    if (!dateString) return null;

    // Extract YYYY-MM-DD portion (allows 1-2 digit month/day for legacy data)
    const match = String(dateString).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) return null;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    // Validate month 1-12
    if (month < 1 || month > 12) {
        return null;
    }

    // Validate day against actual days in month (leap-year aware)
    const maxDay = getDaysInMonth(year, month);
    if (day < 1 || day > maxDay) {
        return null;
    }

    return { year, month, day };
};

/**
 * Formats a date-only string for display WITHOUT timezone conversion.
 * Output: "Jan 15, 2024"
 *
 * @param {string} dateString - Date string in "YYYY-MM-DD" or ISO format
 * @returns {string} Formatted date or empty string if invalid
 */
export const formatDate = (dateString) => {
    const parsed = parseDateOnly(dateString);
    if (!parsed) return '';

    const monthName = MONTH_NAMES[parsed.month - 1];
    const day = String(parsed.day).padStart(2, '0');

    return `${monthName} ${day}, ${parsed.year}`;
};

/**
 * Formats a date-only string for HTML date input (YYYY-MM-DD).
 * Returns the date portion without timezone conversion.
 *
 * @param {string} dateString - Date string in any supported format
 * @returns {string} "YYYY-MM-DD" format or empty string if invalid
 */
/**
 * Formats a date-only string for short display WITHOUT timezone conversion.
 * Output: "Jan 15" (month and day only, no year)
 *
 * @param {string} dateString - Date string in "YYYY-MM-DD" or ISO format
 * @returns {string} Formatted date or empty string if invalid
 */
export const formatDateShort = (dateString) => {
    const parsed = parseDateOnly(dateString);
    if (!parsed) return '';

    const monthName = MONTH_NAMES[parsed.month - 1];
    return `${monthName} ${parsed.day}`;
};

export const toInputDateFormat = (dateString) => {
    const parsed = parseDateOnly(dateString);
    if (!parsed) return '';

    const month = String(parsed.month).padStart(2, '0');
    const day = String(parsed.day).padStart(2, '0');

    return `${parsed.year}-${month}-${day}`;
};
